import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { ANALYTICS_PROJECTS, CREATE_ANALYTICS_PROJECT } from '../graphql/analytics_projects';
import {
  CREATE_ANALYTICS_WORKFLOW,
  UPDATE_ANALYTICS_WORKFLOW,
  DELETE_ANALYTICS_WORKFLOW
} from '../graphql/analytics_workflows';
import { ANALYTICS_CATALOG } from '../graphql/analytics';
import M3Card from '../components/M3Card';
import M3Menu from '../components/M3Menu';
import M3Dialog, { DialogTitle, DialogBody, DialogActions } from '../components/M3Dialog';
import { WORKFLOW_TEMPLATES } from '../components/analytics/workflow/templates';
import '../styles/analytics-dashboard.css';

/** Analytics dashboard: card grid of all projects with their workflows.
 * Clicking a card navigates to the workspace for that workflow. */
export default function AnalyticsDashboard() {
  const { data, loading, refetch } = useQuery(ANALYTICS_PROJECTS);
  const { data: catalogData } = useQuery(ANALYTICS_CATALOG);
  const [createProject] = useMutation(CREATE_ANALYTICS_PROJECT);
  const [createWorkflow] = useMutation(CREATE_ANALYTICS_WORKFLOW);
  const [updateWorkflow] = useMutation(UPDATE_ANALYTICS_WORKFLOW);
  const [deleteWorkflow] = useMutation(DELETE_ANALYTICS_WORKFLOW);

  // Dialog state
  const [projectDialog, setProjectDialog] = useState(false);
  const [workflowDialog, setWorkflowDialog] = useState(false);
  const [renameDialog, setRenameDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [pendingProjectId, setPendingProjectId] = useState(null);
  const [pendingWorkflowId, setPendingWorkflowId] = useState(null);
  const [pendingWorkflowTitle, setPendingWorkflowTitle] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newWorkflowTitle, setNewWorkflowTitle] = useState('');
  const [newWorkflowTemplate, setNewWorkflowTemplate] = useState('');
  const [renameTitle, setRenameTitle] = useState('');

  const projects = (data && data.analyticsProjects) || [];
  const defaultEntity =
    (catalogData && catalogData.analyticsCatalog
      ? catalogData.analyticsCatalog.entities[0].key
      : null) || 'grave';

  const handleCreateProject = () => {
    if (!newProjectName.trim()) return;
    createProject({ variables: { input: { name: newProjectName.trim() } } })
      .then(() => {
        refetch();
      })
      .finally(() => {
        setProjectDialog(false);
        setNewProjectName('');
      });
  };

  const handleCreateWorkflow = () => {
    if (!newWorkflowTitle.trim() || !pendingProjectId) return;
    const template = WORKFLOW_TEMPLATES.find((t) => t.id === newWorkflowTemplate);
    const input = {
      projectId: pendingProjectId,
      title: newWorkflowTitle.trim(),
      entity: template ? template.entity : defaultEntity
    };
    if (template) input.graph = template.build();
    createWorkflow({ variables: { input } }).then((res) => {
      window.location.href = `/analytics/workflows/${res.data.createAnalyticsWorkflow.id}`;
    });
  };

  const handleRenameWorkflow = () => {
    if (!renameTitle.trim() || !pendingWorkflowId) return;
    updateWorkflow({ variables: { input: { id: pendingWorkflowId, title: renameTitle.trim() } } })
      .then(() => refetch())
      .finally(() => {
        setRenameDialog(false);
        setRenameTitle('');
      });
  };

  const handleDeleteWorkflow = () => {
    if (!pendingWorkflowId) return;
    deleteWorkflow({ variables: { input: { id: pendingWorkflowId } } })
      .then(() => refetch())
      .finally(() => {
        setDeleteDialog(false);
        setPendingWorkflowId(null);
      });
  };

  const openWorkflowDialog = (projectId) => {
    setPendingProjectId(projectId);
    setNewWorkflowTitle('');
    setNewWorkflowTemplate('');
    setWorkflowDialog(true);
  };

  const openRenameDialog = (id, title) => {
    setPendingWorkflowId(id);
    setRenameTitle(title);
    setRenameDialog(true);
  };

  const openDeleteDialog = (id, title) => {
    setPendingWorkflowId(id);
    setPendingWorkflowTitle(title);
    setDeleteDialog(true);
  };

  return (
    <div className="analytics-dashboard">
      <header className="analytics-dashboard-header">
        <h1>Analytics Projects</h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setNewProjectName('');
            setProjectDialog(true);
          }}
        >
          + New Project
        </button>
      </header>

      {loading && <p className="text-muted">Loading…</p>}

      {!loading && projects.length === 0 && (
        <M3Card variant="outlined">
          <M3Card.Body>
            <p className="text-muted">No projects yet. Create one to start analysing your data.</p>
          </M3Card.Body>
        </M3Card>
      )}

      <div className="dashboard-grid">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onCreateWorkflow={() => openWorkflowDialog(project.id)}
            onOpenWorkflow={(id) => {
              window.location.href = `/analytics/workflows/${id}`;
            }}
            onRename={(id, title) => openRenameDialog(id, title)}
            onDelete={(id, title) => openDeleteDialog(id, title)}
          />
        ))}
      </div>

      {/* New Project Dialog */}
      <M3Dialog
        open={projectDialog}
        onClose={() => {
          setProjectDialog(false);
          setNewProjectName('');
        }}
        title="New Project"
        size="small"
      >
        <DialogBody>
          <input
            type="text"
            className="sidebar-input"
            placeholder="Project name…"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateProject();
            }}
            autoFocus
          />
        </DialogBody>
        <DialogActions
          primary={{ label: 'Create', onClick: handleCreateProject }}
          secondary={{
            label: 'Cancel',
            onClick: () => {
              setProjectDialog(false);
              setNewProjectName('');
            }
          }}
        />
      </M3Dialog>

      {/* New Workflow Dialog */}
      <M3Dialog
        open={workflowDialog}
        onClose={() => {
          setWorkflowDialog(false);
          setNewWorkflowTitle('');
          setNewWorkflowTemplate('');
        }}
        title="New Workflow"
        size="small"
      >
        <DialogBody>
          <input
            type="text"
            className="sidebar-input"
            placeholder="Workflow title…"
            value={newWorkflowTitle}
            onChange={(e) => setNewWorkflowTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateWorkflow();
            }}
            autoFocus
          />
          <p className="text-muted">Start from a template</p>
          <select
            className="sidebar-input"
            value={newWorkflowTemplate}
            onChange={(e) => setNewWorkflowTemplate(e.target.value)}
          >
            <option value="">Blank (source → table)</option>
            {WORKFLOW_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {(() => {
            const template = WORKFLOW_TEMPLATES.find((t) => t.id === newWorkflowTemplate);
            return template ? <p className="text-muted">{template.description}</p> : null;
          })()}
        </DialogBody>
        <DialogActions
          primary={{ label: 'Create', onClick: handleCreateWorkflow }}
          secondary={{
            label: 'Cancel',
            onClick: () => {
              setWorkflowDialog(false);
              setNewWorkflowTitle('');
            }
          }}
        />
      </M3Dialog>

      {/* Rename Workflow Dialog */}
      <M3Dialog
        open={renameDialog}
        onClose={() => {
          setRenameDialog(false);
          setRenameTitle('');
        }}
        title="Rename Workflow"
        size="small"
      >
        <DialogBody>
          <input
            type="text"
            className="sidebar-input"
            placeholder="Workflow title…"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameWorkflow();
            }}
            autoFocus
          />
        </DialogBody>
        <DialogActions
          primary={{ label: 'Rename', onClick: handleRenameWorkflow }}
          secondary={{
            label: 'Cancel',
            onClick: () => {
              setRenameDialog(false);
              setRenameTitle('');
            }
          }}
        />
      </M3Dialog>

      {/* Delete Workflow Dialog */}
      <M3Dialog
        open={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setPendingWorkflowId(null);
        }}
        title="Delete Workflow"
        size="small"
      >
        <DialogBody>
          <p className="text-muted">
            Delete <strong>{pendingWorkflowTitle}</strong>?
          </p>
        </DialogBody>
        <DialogActions
          primary={{ label: 'Delete', onClick: handleDeleteWorkflow, color: 'error' }}
          secondary={{
            label: 'Cancel',
            onClick: () => {
              setDeleteDialog(false);
              setPendingWorkflowId(null);
            }
          }}
        />
      </M3Dialog>
    </div>
  );
}

/** Single project card with its workflows listed inside. */
function ProjectCard({ project, onCreateWorkflow, onOpenWorkflow, onRename, onDelete }) {
  const workflows = project.workflows || [];

  return (
    <M3Card className="dashboard-card">
      <M3Card.Header title={project.name} />
      <M3Card.Body>
        {workflows.length === 0 && (
          <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '0 0 8px' }}>
            No workflows yet
          </p>
        )}
        <ul className="dashboard-card-list">
          {workflows.map((wf) => (
            <WorkflowItem
              key={wf.id}
              workflow={wf}
              onOpen={() => onOpenWorkflow(wf.id)}
              onRename={() => onRename(wf.id, wf.title)}
              onDelete={() => onDelete(wf.id, wf.title)}
            />
          ))}
        </ul>
        <button type="button" className="dashboard-card-add" onClick={onCreateWorkflow}>
          + Workflow
        </button>
      </M3Card.Body>
    </M3Card>
  );
}

/** Single workflow row with three-dot menu. */
function WorkflowItem({ workflow, onOpen, onRename, onDelete }) {
  const menuItems = [
    { key: 'open', label: 'Open', onClick: onOpen },
    { key: 'rename', label: 'Rename', onClick: onRename },
    {
      key: 'delete',
      label: 'Delete',
      onClick: onDelete,
      style: { color: 'var(--md-sys-color-error)' }
    }
  ];

  return (
    <li className="dashboard-card-item">
      <span
        className="dashboard-card-item-label"
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={onOpen}
        title="Open workflow"
      >
        {workflow.title}
      </span>
      <M3Menu
        trigger={
          <button type="button" className="dashboard-card-menu-btn" aria-label="Actions">
            <span className="material-symbols-rounded">more_vert</span>
          </button>
        }
        items={menuItems}
        anchor="bottom"
      />
    </li>
  );
}
